// Updated Main Component - Ensure Journal Flow Works Properly

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import "./alex.css"

// Import separated components
import GlassNavigation from './components/GlassNavigation';
import AIBuddy from './components/AIBuddy';
import SummaryPanel from './components/SummaryPanel';
import CallTimer from './components/CallTimer';
import TopStatusBar from './components/TopStatusBar';
import VerticalSidebar from './components/VerticalSidebar';

// Import services
import JournalStorageService from './services/JournalStorageService';
import ReminderService from './services/ReminderService';
import DataStoreService from './services/DataStoreService';
import AzureOpenAIService from './services/AzureOpenAIService';
import ConversationStorageManager from './services/ConversationStorageManager';
import ExportService from './services/ExportService';
import AudioService from './services/AudioService';

// Import modals
import { ReminderSetModal, ReminderTriggeredModal } from './components/ReminderComponents';

// Import hooks
import { useEVIConnection } from './hooks/useEviConnection';
import { useReminderSystem } from './hooks/useReminderSystem';
import { useConversationManager } from './hooks/useConversationManager';
import { useSessionTimer } from './hooks/useSessionTimer';
import { useJournalManager } from './hooks/useJournalManager';

const HumeEVIInterface = () => {
  const navigate = useNavigate();

  // Service instances
  const [journalStorageService] = useState(() => new JournalStorageService());
  const [reminderService] = useState(() => new ReminderService());
  const dataStoreService = useRef(new DataStoreService());
  const azureOpenAIService = useRef(null);
  const audioService = useRef(new AudioService());

  // Configuration
  const config = {
    HUME_API_KEY: process.env.REACT_APP_HUME_API_KEY ,
    CONFIG_ID: process.env.REACT_APP_HUME_CONFIG_ID ,
    AZURE_OPENAI_ENDPOINT: process.env.REACT_APP_AZURE_OPENAI_ENDPOINT ,
    AZURE_OPENAI_API_KEY: process.env.REACT_APP_AZURE_OPENAI_API_KEY ,
    AZURE_OPENAI_DEPLOYMENT: process.env.REACT_APP_AZURE_OPENAI_DEPLOYMENT,
    EVI_WS_URL: 'wss://api.hume.ai/v0/evi/chat'
  };
  // Core states
  const [userId] = useState('user_' + Date.now());
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('Listen');

  // Use custom hooks to manage different aspects
  const conversationManager = useConversationManager(userId, dataStoreService.current);
  const sessionTimer = useSessionTimer();
  const journalManager = useJournalManager(
    journalStorageService,
    azureOpenAIService.current,
    conversationManager
  );
  const reminderSystem = useReminderSystem(reminderService, conversationManager);
  const eviConnection = useEVIConnection(config, audioService.current, reminderSystem);

  // Initialize services
  useEffect(() => {
    if (config.AZURE_OPENAI_ENDPOINT && config.AZURE_OPENAI_API_KEY) {
      azureOpenAIService.current = new AzureOpenAIService(
        config.AZURE_OPENAI_ENDPOINT,
        config.AZURE_OPENAI_API_KEY,
        config.AZURE_OPENAI_DEPLOYMENT
      );
    }

    audioService.current.initializeAllAudio();
    if (conversationManager?.loadPreviousConversation) {
      conversationManager.loadPreviousConversation();
    }

    return () => {
      if (eviConnection?.cleanup) eviConnection.cleanup();
      audioService.current.cleanup();
    };
  }, []);

  // =============================================================================
  // DEBUG: Watch for session state changes to trigger journal processing
  // =============================================================================
  useEffect(() => {
    console.log('🔥 [Main] Session state changed:', {
      isRecording: eviConnection.isRecording,
      sessionActive: conversationManager?.sessionActive,
      journalRequested: journalManager.journalRequested,
      takingNoteMessage: journalManager.takingNoteMessage
    });

    // CRITICAL: When recording stops and journal was requested, end the session
    if (!eviConnection.isRecording && journalManager.journalRequested && conversationManager?.sessionActive) {
      console.log('🔥 [Main] Recording stopped with journal request - ending session');
      conversationManager.endSession();
    }
  }, [eviConnection.isRecording, conversationManager?.sessionActive, journalManager.journalRequested]);

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  const handleStartListening = () => {
    console.log('🔥 [Main] Starting to listen');
    if (!eviConnection.isConnected) {
      eviConnection.connectToEVI();
    } else {
      eviConnection.startRecording();
    }
    
    // Start session when recording starts
    if (conversationManager?.startSession) {
      conversationManager.startSession();
    }
    
    setActiveTab('Listen');
  };

  const handleStopListening = () => {
    console.log('🔥 [Main] Stopping listening');
    eviConnection.stopRecording();
    
    // End session when recording stops
    if (conversationManager?.endSession) {
      conversationManager.endSession();
    }
    
    if (sessionTimer?.endSession) {
      sessionTimer.endSession();
    }
  };

  const handleStartOver = async () => {
    console.log('🔥 [Main] Starting over');
    if (sessionTimer?.sessionActive && sessionTimer?.endSession) {
      await sessionTimer.endSession();
    }
    
    audioService.current.stopAllAudio();
    
    if (eviConnection?.reset) eviConnection.reset();
    if (conversationManager?.clearConversation) conversationManager.clearConversation();
    if (journalManager?.clearJournal) journalManager.clearJournal();
  };

const handleJournalClick = () => {
  console.log('🔥 [Main] Journal button clicked');
  
  if (eviConnection.isRecording) {
    // During recording - show "taking note" animation
    console.log('🔥 [Main] Recording active - showing taking note animation');
    journalManager.setTakingNoteMessage(true);
    
    setTimeout(() => {
      journalManager.setTakingNoteMessage(false);
      journalManager.setJournalRequested(true);
      journalManager.setJournalButtonClicked(true);
    }, 2000);
    
  } else {
    // Not recording - user wants to view journal (show "Loading...")
    console.log('🔥 [Main] Not recording - user requesting to view journal');
    
    // Show loading state instead of taking note
    journalManager.setIsLoadingJournal(true);
    
    setTimeout(async () => {
      try {
        // Process the summary
        const hasConversation = conversationManager?.conversation?.length > 0;
        
        if (!hasConversation) {
          console.log('🔥 [Main] No conversation - generating empty summary');
          await journalManager.generateSummaryForEmptyConversation();
        } else {
          console.log('🔥 [Main] Processing existing conversation');
          if (journalManager.processSummary) {
            await journalManager.processSummary();
          }
        }
        
        // Show the summary panel
        journalManager.setShowSummary(true);
        journalManager.setJournalButtonClicked(true);
        
      } catch (error) {
        console.error('Error processing journal:', error);
      } finally {
        // Always clear loading state
        journalManager.setIsLoadingJournal(false);
      }
    }, 1500);
  }

  setActiveTab('journal');
};

  const handleReminderClick = () => {
    console.log('🔥 [Main] Reminder button clicked, active reminders:', reminderSystem?.activeReminders?.length || 0);
    setActiveTab('reminders'); 
    journalManager.setShowSummary(true); // Show the dropdown panel
  };

  const toggleSound = () => {
    audioService.current.toggleSound();
  };

  // Determine if session is active (recording or connected with conversation)
  const isSessionActive = conversationManager?.sessionActive || eviConnection.isRecording;

  // =============================================================================
  // TESTING COMPONENT (remove in production)
  // =============================================================================
  const JournalFlowTest = () => (
    <div>

    </div>
  );

  return (
    <div className="hume-evi-interface">
      {/* Enhanced Debug Panel */}
      <div style={{ 
        position: 'fixed', 
        top: 0, 
        right: 0, 
        background: 'rgba(0,0,0,0.9)', 
        color: 'white', 
        padding: '15px', 
        fontSize: '11px',
        zIndex: 9999,
        maxWidth: '350px',
        borderRadius: '0 0 0 10px'
      }}>
        <div><strong>🔥 JOURNAL FLOW DEBUG</strong></div>
        <div>Recording: {eviConnection.isRecording ? '✅ YES' : '❌ NO'}</div>
        <div>Session Active: {conversationManager?.sessionActive ? '✅ YES' : '❌ NO'}</div>
        <div>Taking Note: {journalManager.takingNoteMessage ? '✅ YES' : '❌ NO'}</div>
        <div>Button Clicked: {journalManager.journalButtonClicked ? '✅ YES' : '❌ NO'}</div>
        <div>Journal Requested: {journalManager.journalRequested ? '✅ YES' : '❌ NO'}</div>
        <div>Show Summary: {journalManager.showSummary ? '✅ YES' : '❌ NO'}</div>
        <div>Show View Button: {journalManager.showViewJournalButton ? '✅ YES' : '❌ NO'}</div>
        <div>Conversation: {conversationManager?.conversation?.length || 0} msgs</div>
        <div>Has Content: {conversationManager?.hasMeaningfulContent?.() ? '✅ YES' : '❌ NO'}</div>
        <div>Active Tab: {activeTab}</div>
      </div>

      {/* Test Component */}
      <JournalFlowTest />

      {/* Reminder Modals */}
      {reminderSystem && (
        <>
          <ReminderSetModal
            reminder={reminderSystem.currentReminder}
            isVisible={reminderSystem.showReminderSetModal}
            onClose={reminderSystem.handleCloseReminderSetModal}
          />
          
          <ReminderTriggeredModal
            reminder={reminderSystem.currentReminder}
            isVisible={reminderSystem.showReminderTriggeredModal}
            onClose={reminderSystem.handleCloseReminderTriggeredModal}
            onSnooze={reminderSystem.handleReminderSnooze}
          />
        </>
      )}

      <div className="quiet-one-container">
        
        <VerticalSidebar />
        
        <div className="main-content">
          <TopStatusBar
            currentEmotion={eviConnection.currentEmotion}
            sessionActive={sessionTimer?.sessionActive}
            sessionStartTime={sessionTimer?.sessionStartTime}
          />

          <div className='ai-alex-buddy-container'>
            <AIBuddy
              isRecording={eviConnection.isRecording}
              isEviSpeaking={eviConnection.isEviSpeaking}
              isConnected={eviConnection.isConnected}
              startupSoundPlaying={audioService.current.getAudioStates?.().startupSoundPlaying || false}
              preResponseSoundPlaying={audioService.current.getAudioStates?.().preResponseSoundPlaying || false}
              isInitializing={eviConnection.isInitializing}
              autoSummaryPending={journalManager.isGeneratingSummary}
              onBuddyClick={eviConnection.isRecording ? handleStopListening : handleStartListening}
              response={eviConnection.response}
              showResponse={eviConnection.showResponse}
            />
          </div>

          {/* Call Timer - only show when recording or call ended */}
          {(eviConnection.isRecording || sessionTimer?.callEnded) && (
            <CallTimer
              timeRemaining={sessionTimer?.timeRemaining}
              callEnded={sessionTimer?.callEnded}
            />
          )}

          {/* Summary Panel */}
          {journalManager.showSummary && (
            <SummaryPanel
              activeTab={activeTab}
              isGeneratingSummary={journalManager.isGeneratingSummary}
              conversationSummary={journalManager.conversationSummary}
              emotionalInsights={journalManager.emotionalInsights}
              summaryHeader={journalManager.summaryHeader}
              displayedHeader={journalManager.displayedHeader}
              displayedSummary={journalManager.displayedSummary}
              displayedInsights={journalManager.displayedInsights}
              isAutoSaved={journalManager.isAutoSaved}
              activeReminders={reminderSystem?.activeReminders || []}
              takingNoteMessage={journalManager.takingNoteMessage}
              onClose={() => {
                journalManager.setShowSummary(false);
                setActiveTab('Listen'); // Reset to default tab
              }}
              onDownload={journalManager.downloadJournal}
              onViewJournal={() => navigate('/journal')}
              onCancelReminder={reminderSystem?.handleReminderCancel}
              reminderService={reminderService}
              journalRequested={journalManager.journalRequested}
              sessionActive={conversationManager?.sessionActive}
            />
          )}
        </div>

<GlassNavigation
  activeTab={activeTab}
  isRecording={eviConnection.isRecording}
  callEnded={sessionTimer?.callEnded}
  timeRemaining={sessionTimer?.timeRemaining}
  isGeneratingSummary={journalManager.isGeneratingSummary}
  takingNoteMessage={journalManager.takingNoteMessage}
  isLoadingJournal={journalManager.isLoadingJournal} // Add this line
  journalButtonClicked={journalManager.journalButtonClicked}
  journalRequested={journalManager.journalRequested}
  activeReminders={reminderSystem?.activeReminders || []}
  soundEnabled={audioService.current.getAudioStates?.().soundEnabled || true}
  sessionActive={eviConnection.isRecording}
  onStartListening={handleStartListening}
  onStopListening={handleStopListening}
  onStartOver={handleStartOver}
  onJournalClick={handleJournalClick}
  onReminderClick={handleReminderClick}
  onToggleSound={toggleSound}
/>
      </div>

      {/* Error and Success Messages */}
      {error && (
        <div className="error-message" style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#ff4444',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '8px',
          zIndex: 1000
        }}>
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="success-message" style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#4CAF50',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '8px',
          zIndex: 1000
        }}>
          {successMessage}
        </div>
      )}
    </div>
  );
};

export default HumeEVIInterface;